<template lang="pug">
.input-wrapper
  label.input-label.p-small(:for='inputId', v-if='label') {{ label }} {{ requiredSymbol }}
  Icon.icon.ms-2.align-text-bottom(
    v-if='tooltipTitle',
    name='circle-question',
    data-bs-html='true',
    data-bs-toggle='tooltip',
    data-bs-placement='right',
    :title='tooltipTitle',
    :style='"line-height: 1.65rem"'
  )
  //- Simple Input Text
  div(
    :class='[icon === "" ? "" : "form-control-icon"]',
    v-if='type === "text"'
  )
    Icon.icon(
      v-if='!!icon',
      :name='icon',
      :style='"width: 1.25rem; height: 1.5rem;" + `color: var(--color-${getColorIcon}); `'
    )
    input(
      :name='inputName',
      :id='inputId',
      :value='modelValue',
      @input='$emit("update:modelValue", $event.target.value)',
      @focus='$emit("focused")',
      @blur='$emit("blured")',
      :class='["form-control", errorLog ? "invalid" : ""]',
      :placeholder='placeholder',
      :disabled='isDisabled',
      :autocomplete='autocomplete',
      :type='isNumeric ? "tel" : "text"',
      v-maska='maskaOptions',
      :maxlength='maxLength',
      :minlength='minLength'
    )
    Icon.icon-right(
      v-if='iconRight',
      @click='iconRightAction',
      :name='iconRight',
      :color='iconColor',
      style='width: 1.25rem; height: 1.25rem; cursor: pointer'
    )
  //- Input Password
  div(v-if='type === "password"')
    .form-control-icon
      Icon.icon(
        name='lock-keyhole',
        :style='"width: 1.25rem; height: 1.5rem;" + `color: var(--color-${getColorIcon}); `'
      )
      input(
        :value='modelValue',
        :name='inputName',
        :id='inputId',
        @input='$emit("update:modelValue", $event.target.value)',
        @focus='$emit("focused")',
        @blur='$emit("blured")',
        :type='showPassword ? "text" : "password"',
        :class='["form-control", errorLog ? "invalid" : ""]',
        :placeholder='placeholder',
        :disabled='isDisabled',
        :maxlength='maxLength',
        :minlength='minLength'
      )
      Icon.icon-right(
        v-if='!showPassword',
        @click='toggleShowPassword',
        name='eye',
        :color='iconColor',
        style='width: 1.35rem; height: 1.5rem'
      )
      Icon.icon-right(
        v-else,
        @click='toggleShowPassword',
        name='eye-slash',
        color='--color-dark-gray',
        style='width: 1.35rem; height: 1.5rem'
      )
  //- Input Phone with mask
  div(v-if='type === "phone"')
    .form-control-icon
      img.icon(src='@/assets/images/icons-forms.svg')
      input(
        :value='modelValue',
        @input='$emit("update:modelValue", $event.target.value)',
        @focus='$emit("focused")',
        @blur='$emit("blured")',
        :name='inputName',
        :id='inputId',
        v-maska='{ mask: ["+55 (##) ####-####", "+55 (##) #####-####"] }',
        :class='["form-control", errorLog ? "invalid" : ""]',
        :placeholder='placeholder',
        :disabled='isDisabled',
        :autocomplete='autocomplete',
        :maxlength='maxLength',
        :minlength='minLength'
      )
  //- Message if has error
  .invalid-feedback(v-if='!!errorLog')
    span.text {{ errorLog }}
</template>

<script lang="ts">
  import { defineComponent, PropType } from 'vue'
  import Icon from '@/components/Atoms/Icon.vue'

  export default defineComponent({
    name: 'InputForm',
    props: {
      label: {
        type: String,
      },
      id: {
        type: String,
        default: '',
      },
      name: {
        type: String,
        default: '',
      },
      placeholder: {
        type: String,
        default: '',
      },
      errorLog: {
        type: String,
        default: '',
      },
      isDisabled: {
        type: Boolean,
      },
      icon: {
        type: String,
        default: '',
      },
      iconRight: {
        type: String,
        optional: true,
      },
      iconRightAction: {
        type: Function as PropType<() => void>,
        default: () => void 0,
      },
      iconColor: {
        type: String,
        default: 'primary',
      },
      type: {
        type: String,
        default: 'text',
      },
      isRequired: {
        type: Boolean,
        default: false,
      },
      /**
       * Máscara de formatação de input (maska 3.x)
       *
       * IMPORTANTE: Use a prop :mask, NÃO use v-maska diretamente neste componente!
       * A diretiva v-maska aplicada em componentes Vue não funciona (apenas em <input> nativos).
       *
       * @param {Array<string>} mask - Array com padrões de máscara
       * @example
       * // CPF
       * :mask="['###.###.###-##', '']"
       *
       * @example
       * // CNPJ
       * :mask="['##.###.###/####-##', '']"
       *
       * @example
       * // OTP / Código
       * :mask="['### ###', '']"
       *
       * @example
       * // Telefone (use type="phone" que já tem máscara hardcoded)
       * <Input type="phone" v-model="telefone" />
       *
       * @see {@link https://github.com/beholdr/maska|Documentação maska 3.x}
       * @see {@link ../../../docs/MASK_USAGE.md|Guia de uso de máscaras}
       */
      mask: {
        type: Array as PropType<Array<string>>,
      },
      options: {
        type: Array as PropType<Array<string>>,
      },
      modelValue: {
        type: String,
      },
      autocomplete: {
        type: String,
        default: 'on',
      },
      isNumeric: {
        type: Boolean,
      },
      tooltipTitle: {
        type: String,
      },
      maxLength: {
        type: String,
      },
      minLength: {
        type: String,
      },
    },
    components: { Icon },
    data: () => ({
      showPassword: false,
    }),
    computed: {
      requiredSymbol() {
        return this.$props.isRequired && this.$props.label ? '*' : ''
      },
      getColorIcon() {
        if (this.$props.isDisabled) {
          return 'gray-dark'
        }

        if (this.$props.errorLog !== '') {
          return 'danger'
        }

        if (this.$props.iconColor) {
          return `${this.$props.iconColor}`
        }

        return 'primary'
      },
      inputId() {
        return (
          this.$props.id ||
          this.$props.name ||
          this.$props.label?.toLowerCase() ||
          ''
        )
      },
      inputName() {
        return (
          this.$props.name ||
          this.$props.id ||
          this.$props.label?.toLowerCase() ||
          ''
        )
      },
      /**
       * Converte array de máscara para formato objeto maska 3.x
       *
       * maska 3.x requer: v-maska="{ mask: ['###', ''] }"
       * Para simplificar, aceitamos array na prop: :mask="['###', '']"
       * Este computed faz a conversão: ['###'] → { mask: ['###'] }
       *
       * Motivos para usar array na prop:
       * 1. Compatibilidade com configurações JSON existentes
       * 2. Sintaxe mais simples nos templates
       * 3. Consistência com todo o codebase
       *
       * @returns {Object|undefined} Objeto maska 3.x ou undefined (sem máscara)
       */
      maskaOptions() {
        if (!this.$props.mask || this.$props.mask.length === 0) {
          return undefined
        }
        return { mask: this.$props.mask }
      },
    },
    methods: {
      toggleShowPassword() {
        this.showPassword = !this.showPassword
      },
      updateValue(value: string) {
        this.$emit('input', value)
      },
    },
  })
</script>

<style scoped lang="stylus">
  .input-wrapper
    text-align: left
    label
      padding-bottom: 0.5rem
  .invalid-feedback
    display: inline-block
    text-align: left

  .icon-right
      position: absolute
      top: 0
      bottom: 0
      left: auto
      font-size: 1rem
      margin: auto
      right: 1rem
      color: var(--color-form-icon)
      z-index: 1
</style>
